/**
 * Country Context
 *
 * Global state for country selection and country list.
 * Fetches countries once on mount and provides:
 * - The full list of active countries (cached)
 * - The currently selected country ID (persisted in localStorage)
 * - Helper to get the full Country object
 *
 * For non-SUPER_ADMIN roles, the staff member's assigned country is used
 * automatically and the selector is disabled.
 *
 * This eliminates the need for each page to independently fetch countries
 * and maintains the selection across route changes.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { countryService } from '@/services/country.service';
import { useAuth } from '@/contexts/AuthContext';
import type { Country } from '@/types/country.types';

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEY = 'admin:selectedCountryId';

// ============================================================================
// TYPES
// ============================================================================

interface CountryContextType {
  /** All available countries (fetched once on mount). */
  countries: Country[];
  /** Whether countries are still being loaded. */
  isLoadingCountries: boolean;
  /** Currently selected country ID (persisted in localStorage). */
  selectedCountryId: string;
  /** Set the selected country ID. */
  setSelectedCountryId: (id: string) => void;
  /** Convenience: the full Country object for the current selection. */
  selectedCountry: Country | null;
  /** Re-fetch countries (e.g. after CRUD on countries page). */
  refreshCountries: () => Promise<void>;
  /** Whether the current user is SUPER_ADMIN (can switch countries freely). */
  isSuperAdmin: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const CountryContext = createContext<CountryContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface CountryProviderProps {
  children: ReactNode;
}

export function CountryProvider({ children }: CountryProviderProps) {
  const { user } = useAuth();
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [selectedCountryId, setSelectedCountryIdRaw] = useState<string>(() => {
    // Restore from localStorage on first render
    return localStorage.getItem(STORAGE_KEY) || '';
  });

  const staffRole = user?.profiles?.staff?.role;
  const staffCountryId = user?.profiles?.staff?.countryId;
  const isSuperAdmin = staffRole === 'SUPER_ADMIN';

  // Persist selection to localStorage
  const setSelectedCountryId = useCallback((id: string) => {
    // Non-SUPER_ADMIN cannot change country — always locked to their assigned one
    if (!isSuperAdmin) return;
    setSelectedCountryIdRaw(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isSuperAdmin]);

  // Fetch countries from API
  const fetchCountries = useCallback(async () => {
    setIsLoadingCountries(true);
    try {
      const response = await countryService.getAll({ limit: 100 });
      setCountries(response.data);

      if (isSuperAdmin) {
        // SUPER_ADMIN: validate that the persisted selection still exists
        const persisted = localStorage.getItem(STORAGE_KEY);
        if (persisted && !response.data.some((c) => c.id === persisted)) {
          // Country was deleted or deactivated — clear selection
          setSelectedCountryIdRaw('');
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch {
      console.error('[CountryContext] Failed to fetch countries');
    } finally {
      setIsLoadingCountries(false);
    }
  }, [isSuperAdmin]);

  // For non-SUPER_ADMIN: auto-select their assigned country
  useEffect(() => {
    if (!isSuperAdmin && staffCountryId) {
      setSelectedCountryIdRaw(staffCountryId);
    }
  }, [isSuperAdmin, staffCountryId]);

  // Load on mount
  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // Derived: full Country object for the current selection
  const selectedCountry = useMemo(
    () => countries.find((c) => c.id === selectedCountryId) ?? null,
    [countries, selectedCountryId],
  );

  const value = useMemo<CountryContextType>(
    () => ({
      countries,
      isLoadingCountries,
      selectedCountryId,
      setSelectedCountryId,
      selectedCountry,
      refreshCountries: fetchCountries,
      isSuperAdmin,
    }),
    [
      countries,
      isLoadingCountries,
      selectedCountryId,
      setSelectedCountryId,
      selectedCountry,
      fetchCountries,
      isSuperAdmin,
    ],
  );

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useCountry(): CountryContextType {
  const ctx = useContext(CountryContext);
  if (!ctx) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return ctx;
}
